import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Fab,
  Paper,
  Typography,
  IconButton,
  TextField,
  Tooltip,
  Zoom,
  Slide,
  CircularProgress,
  Button,
} from '@mui/material';
import SmartToyIcon from '@mui/icons-material/SmartToy';
import CloseIcon from '@mui/icons-material/Close';
import SendIcon from '@mui/icons-material/Send';
import WhatsAppIcon from '@mui/icons-material/WhatsApp';
import SupportAgentIcon from '@mui/icons-material/SupportAgent';
import * as signalR from '@microsoft/signalr';
import { useTranslation } from 'react-i18next';
import { NETAPI_URL, AIAPI_URL } from '../../../config/api';
import { BRAND_GRADIENTS } from '../../../config/brand';

// ============================================================================
// PUBLIC AI CHAT WIDGET — welcome sitesi (login öncesi) ön-satış asistanı
// ============================================================================
// Login'li AiChatFab/AiChatWindow'dan bağımsız, sade bir varyant:
//  - Auth YOK: doğrudan fetch ile /api/PublicAiChat/* çağrılır (aiApi
//    interceptor'ları token/company header eklediği için kullanılmaz).
//  - Konuşma geçmişi yalnızca istemcidedir; sunucu saklamaz.
//  - Asistan yalnızca AQUASOFT tanıtımı/fiyat/uygunluk cevaplar (backend promptu).
//  - FAB her zaman görünür (WhatsApp FAB'ının eski yeri: sağ alt köşe).
//  - WhatsApp erişimi ayrı buton yerine chat penceresinin başlığında.
//
// CANLI DESTEK MODU: Ziyaretçi aynı pencerede gerçek bir yetkiliyle konuşabilir.
//  - POST /api/SupportChat/start → SignalR /hubs/support → JoinSession.
//  - Canlı destek ANA API'de (NETAPI_URL) barındırılır: ordershub gibi prod
//    reverse proxy'nin WebSocket desteği bu domain'de hazır. AI sohbeti
//    (PublicAiChat) gts_ai_api'de barınır (REST-only, WebSocket gerektirmez).
//  - Talep tüm açık SuperAdmin sayfalarına düşer; ilk devralan yetkili görüşür.
//  - sessionId sessionStorage'da tutulur (sayfa yenilemede görüşmeye devam).
//  - Mesaj akışı: kendi mesajlarımız optimistik eklenir; hub'dan gelen
//    'visitor' yayınları (kendi mesajlarımızın ekosu) atlanır.
// ============================================================================

const WHATSAPP_PHONE = '905323095062';
const WHATSAPP_MESSAGE = 'Merhaba! Web sitenizden yazıyorum. Kiosk ve ön muhasebe çözümleriniz hakkında bilgi alabilir miyim?';
const SUPPORT_SESSION_KEY = 'publicSupportSessionId';
/** Bu süre içinde kimse devralmazsa telefon numaralı bilgi mesajı gösterilir. */
const SUPPORT_WAIT_NOTICE_MS = 2 * 60 * 1000;

const openWhatsApp = () => {
  const encoded = encodeURIComponent(WHATSAPP_MESSAGE);
  const isMobile = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
  const url = isMobile
    ? `https://wa.me/${WHATSAPP_PHONE}?text=${encoded}`
    : `https://web.whatsapp.com/send?phone=${WHATSAPP_PHONE}&text=${encoded}`;
  window.open(url, '_blank', 'noopener,noreferrer');
};

interface PublicChatMessage {
  id: string;
  role: 'user' | 'assistant';
  content: string;
  isError?: boolean;
  /** Canlı destek durum mesajı (ortalanmış, gri) */
  isSystem?: boolean;
  /** Canlı destekte konuşan yetkilinin adı (balonun üstünde gösterilir) */
  senderName?: string;
}

/** SignalR/API alanlarını hem camelCase hem PascalCase okuyabilen yardımcı. */
const pick = (obj: any, key: string): any =>
  obj?.[key] ?? obj?.[key.charAt(0).toUpperCase() + key.slice(1)];

/** **kalın** işaretlerini <strong>'a çevirir. */
const renderInline = (text: string): React.ReactNode[] =>
  text.split(/(\*\*[^*]+\*\*)/g).map((seg, i) =>
    seg.startsWith('**') && seg.endsWith('**') ? (
      <strong key={i}>{seg.slice(2, -2)}</strong>
    ) : (
      seg
    )
  );

/** Hafif markdown: satırlar, ###/## başlık → kalın, **bold**. */
const renderAssistantText = (content: string): React.ReactNode => (
  <Typography variant="body2" component="div" sx={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
    {content.split('\n').map((line, i) => {
      const heading = line.match(/^#{1,4}\s+(.*)$/);
      return (
        <React.Fragment key={i}>
          {i > 0 && <br />}
          {heading ? <strong>{renderInline(heading[1])}</strong> : renderInline(line)}
        </React.Fragment>
      );
    })}
  </Typography>
);

const PublicAiChatWidget: React.FC = () => {
  const { t } = useTranslation('common');
  const navigate = useNavigate();

  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState<PublicChatMessage[]>([]);
  const [text, setText] = useState('');
  const [sending, setSending] = useState(false);
  // 5 sn'den uzun süren AI cevaplarında "yoğunluk nedeniyle gecikme olabilir" bilgisi
  const [slowNotice, setSlowNotice] = useState(false);
  const slowNoticeTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const listRef = useRef<HTMLDivElement | null>(null);

  // ---- Canlı destek durumu ----
  const [mode, setMode] = useState<'ai' | 'support'>('ai');
  const [supportStatus, setSupportStatus] = useState<'waiting' | 'active' | null>(null);
  const [agentName, setAgentName] = useState<string | null>(null);
  const [supportStarting, setSupportStarting] = useState(false);
  const sessionIdRef = useRef<number | null>(null);
  const hubRef = useRef<signalR.HubConnection | null>(null);
  const waitNoticeTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const pushMessage = useCallback((msg: PublicChatMessage) => {
    setMessages((prev) => [...prev, msg]);
  }, []);

  const clearWaitNotice = () => {
    if (waitNoticeTimerRef.current) {
      clearTimeout(waitNoticeTimerRef.current);
      waitNoticeTimerRef.current = null;
    }
  };

  const disconnectHub = useCallback(() => {
    clearWaitNotice();
    const hub = hubRef.current;
    hubRef.current = null;
    if (hub) hub.stop().catch(() => undefined);
  }, []);

  /** Destek görüşmesi bittiğinde AI moduna dönüş (görüşme geçmişi ekranda kalır). */
  const endSupportMode = useCallback(
    (noticeKey: string | null) => {
      disconnectHub();
      sessionIdRef.current = null;
      sessionStorage.removeItem(SUPPORT_SESSION_KEY);
      setMode('ai');
      setSupportStatus(null);
      setAgentName(null);
      if (noticeKey) {
        pushMessage({ id: `sys_${Date.now()}`, role: 'assistant', content: t(noticeKey), isSystem: true });
      }
    },
    [disconnectHub, pushMessage, t]
  );

  /** SignalR bağlantısı kurar ve görüşme grubuna katılır. */
  const connectHub = useCallback(
    async (sessionId: number) => {
      disconnectHub();

      const connection = new signalR.HubConnectionBuilder()
        .withUrl(`${NETAPI_URL}/hubs/support`, {
          // Prod IIS/proxy WebSocket upgrade'i ve SSE'yi geçirmiyor (handshake
          // canceled + "No Connection with that ID" 404). LongPolling doğrulandı;
          // SSE atlanır: WS başarısız olursa doğrudan LongPolling'e düşülür.
          transport: signalR.HttpTransportType.WebSockets | signalR.HttpTransportType.LongPolling,
        })
        .withAutomaticReconnect()
        .configureLogging(signalR.LogLevel.None)
        .build();

      connection.on('NewMessage', (data: any) => {
        if (pick(data, 'sessionId') !== sessionIdRef.current) return;
        const sender = pick(data, 'sender');
        // Kendi mesajlarımız optimistik eklendi — ekoyu atla
        if (sender === 'visitor') return;
        pushMessage({
          id: `s_${pick(data, 'id')}`,
          role: 'assistant',
          content: String(pick(data, 'content') ?? ''),
          isSystem: sender === 'system',
          senderName: sender === 'agent' ? pick(data, 'senderName') ?? undefined : undefined,
        });
      });

      connection.on('SessionClaimed', (data: any) => {
        if (pick(data, 'sessionId') !== sessionIdRef.current) return;
        clearWaitNotice();
        setSupportStatus('active');
        setAgentName(pick(data, 'claimedByName') ?? null);
        // "X bağlandı" sistem mesajı NewMessage yayınıyla ayrıca gelir
      });

      connection.on('SessionClosed', (data: any) => {
        if (pick(data, 'sessionId') !== sessionIdRef.current) return;
        endSupportMode('publicAiChat.supportClosed');
      });

      connection.onreconnected(() => {
        const id = sessionIdRef.current;
        if (id != null) connection.invoke('JoinSession', id).catch(() => undefined);
      });

      await connection.start();
      await connection.invoke('JoinSession', sessionId);
      hubRef.current = connection;
    },
    [disconnectHub, endSupportMode, pushMessage]
  );

  /** Canlı destek başlat: talep aç + hub'a bağlan + bekleme bildirimi kur. */
  const startSupport = useCallback(async () => {
    if (mode === 'support' || supportStarting) return;
    setSupportStarting(true);
    try {
      const res = await fetch(`${NETAPI_URL}/api/SupportChat/start`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({}),
      });
      const data = await res.json().catch(() => null);
      const sessionId = pick(data, 'sessionId');
      if (!res.ok || !sessionId) throw new Error('start failed');

      sessionIdRef.current = sessionId;
      sessionStorage.setItem(SUPPORT_SESSION_KEY, String(sessionId));
      setMode('support');
      setSupportStatus('waiting');
      setAgentName(null);
      pushMessage({ id: `sys_${Date.now()}`, role: 'assistant', content: t('publicAiChat.supportWaiting'), isSystem: true });

      await connectHub(sessionId);

      clearWaitNotice();
      waitNoticeTimerRef.current = setTimeout(() => {
        // Hâlâ bekliyorsa telefon numaralı bilgi (görüşme açık kalır)
        pushMessage({ id: `sys_${Date.now()}`, role: 'assistant', content: t('publicAiChat.supportNoAgent'), isSystem: true });
      }, SUPPORT_WAIT_NOTICE_MS);
    } catch {
      endSupportMode(null);
      pushMessage({ id: `e_${Date.now()}`, role: 'assistant', content: t('publicAiChat.supportError'), isError: true });
    } finally {
      setSupportStarting(false);
    }
  }, [mode, supportStarting, connectHub, endSupportMode, pushMessage, t]);

  /** Sayfa yenileme sonrası açık görüşmeye devam. */
  const resumeSupport = useCallback(async () => {
    const stored = sessionStorage.getItem(SUPPORT_SESSION_KEY);
    const sessionId = stored ? parseInt(stored, 10) : NaN;
    if (!stored || isNaN(sessionId)) return;

    try {
      const res = await fetch(`${NETAPI_URL}/api/SupportChat/session/${sessionId}`);
      const data = await res.json().catch(() => null);
      const session = pick(data, 'session');
      const status = pick(session, 'status');
      if (!res.ok || !session || status === 'closed') {
        sessionStorage.removeItem(SUPPORT_SESSION_KEY);
        return;
      }

      sessionIdRef.current = sessionId;
      setMode('support');
      setSupportStatus(status === 'active' ? 'active' : 'waiting');
      setAgentName(pick(session, 'claimedByName') ?? null);

      const history: PublicChatMessage[] = (pick(data, 'messages') ?? []).map((m: any) => {
        const sender = pick(m, 'sender');
        return {
          id: `s_${pick(m, 'id')}`,
          role: sender === 'visitor' ? ('user' as const) : ('assistant' as const),
          content: String(pick(m, 'content') ?? ''),
          isSystem: sender === 'system',
          senderName: sender === 'agent' ? pick(m, 'senderName') ?? undefined : undefined,
        };
      });
      setMessages(history);

      await connectHub(sessionId);
    } catch {
      sessionStorage.removeItem(SUPPORT_SESSION_KEY);
    }
  }, [connectHub]);

  // İlk açılışta: açık destek görüşmesi varsa devam et, yoksa karşılama mesajı
  useEffect(() => {
    if (open && messages.length === 0) {
      setMessages([{ id: 'greet', role: 'assistant', content: t('publicAiChat.greeting') }]);
      resumeSupport();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  // Unmount: hub bağlantısını kapat (görüşme sunucuda açık kalır) + gecikme uyarısı zamanlayıcısını temizle
  useEffect(
    () => () => {
      disconnectHub();
      if (slowNoticeTimerRef.current) clearTimeout(slowNoticeTimerRef.current);
    },
    [disconnectHub]
  );

  useEffect(() => {
    listRef.current?.scrollTo({ top: listRef.current.scrollHeight, behavior: 'smooth' });
  }, [messages, sending, slowNotice]);

  // =====================================================================
  // Gönderim
  // =====================================================================

  const sendSupportMessage = async (trimmed: string) => {
    pushMessage({ id: `u_${Date.now()}`, role: 'user', content: trimmed });
    setText('');
    setSending(true);
    try {
      const res = await fetch(`${NETAPI_URL}/api/SupportChat/message`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ SessionId: sessionIdRef.current, Content: trimmed }),
      });
      if (!res.ok) {
        const data = await res.json().catch(() => null);
        if (pick(data, 'code') === 'SESSION_CLOSED') {
          endSupportMode('publicAiChat.supportClosed');
        } else {
          pushMessage({ id: `e_${Date.now()}`, role: 'assistant', content: t('publicAiChat.error'), isError: true });
        }
      }
    } catch {
      pushMessage({ id: `e_${Date.now()}`, role: 'assistant', content: t('publicAiChat.error'), isError: true });
    } finally {
      setSending(false);
    }
  };

  const sendAiMessage = async (trimmed: string) => {
    const userMsg: PublicChatMessage = { id: `u_${Date.now()}`, role: 'user', content: trimmed };
    // Sunucuya gönderilen kısa geçmiş: karşılama, hata ve destek mesajları hariç son 8 mesaj
    const history = messages
      .filter((m) => m.id !== 'greet' && !m.isError && !m.isSystem && !m.senderName)
      .slice(-8)
      .map((m) => ({ role: m.role, content: m.content }));

    pushMessage(userMsg);
    setText('');
    setSending(true);
    slowNoticeTimerRef.current = setTimeout(() => setSlowNotice(true), 5000);
    try {
      const res = await fetch(`${AIAPI_URL}/api/PublicAiChat/send`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ Text: trimmed, History: history }),
      });
      const data = await res.json().catch(() => null);
      const success = data?.Success === true || data?.success === true;
      const reply = data?.Reply ?? data?.reply;

      if (success && reply) {
        pushMessage({ id: `a_${Date.now()}`, role: 'assistant', content: String(reply) });
      } else {
        const code = data?.Code ?? data?.code;
        if (code === 'LIMIT_QUESTIONS' || code === 'RATE_LIMIT') {
          // LIMIT_QUESTIONS: günlük soru hakkı doldu (kota rakamı müşteriye söylenmez).
          // RATE_LIMIT: Gemini sağlayıcı kotası doldu (ör. ücretsiz plan 20 istek/gün).
          // İletişime yönlendir + canlı destek seçeneği sun (buton hızlı aksiyonlarda).
          pushMessage({ id: `a_${Date.now()}`, role: 'assistant', content: t('publicAiChat.contactLimit') });
          pushMessage({ id: `a2_${Date.now()}`, role: 'assistant', content: t('publicAiChat.supportSuggest'), isSystem: true });
        } else {
          const msgKey =
            code === 'LIMIT_IP' || code === 'LIMIT_GLOBAL'
              ? 'publicAiChat.limitReached'
              : code === 'AI_BUSY'
                ? 'publicAiChat.busy'
                : 'publicAiChat.error';
          pushMessage({ id: `e_${Date.now()}`, role: 'assistant', content: t(msgKey), isError: true });
        }
      }
    } catch {
      pushMessage({ id: `e_${Date.now()}`, role: 'assistant', content: t('publicAiChat.error'), isError: true });
    } finally {
      if (slowNoticeTimerRef.current) {
        clearTimeout(slowNoticeTimerRef.current);
        slowNoticeTimerRef.current = null;
      }
      setSlowNotice(false);
      setSending(false);
    }
  };

  const handleSend = async () => {
    const trimmed = text.trim();
    if (!trimmed || sending) return;
    if (mode === 'support') {
      await sendSupportMessage(trimmed);
    } else {
      await sendAiMessage(trimmed);
    }
  };

  /** Ziyaretçi görüşmeyi bitirir. */
  const handleEndSupport = async () => {
    const sessionId = sessionIdRef.current;
    endSupportMode('publicAiChat.supportClosed');
    if (sessionId != null) {
      fetch(`${NETAPI_URL}/api/SupportChat/close`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ SessionId: sessionId }),
      }).catch(() => undefined);
    }
  };

  const inSupport = mode === 'support';
  const headerTitle = inSupport ? t('publicAiChat.supportTitle') : t('publicAiChat.title');
  const headerSubtitle = inSupport
    ? supportStatus === 'active' && agentName
      ? agentName
      : t('publicAiChat.supportWaitingSubtitle')
    : t('publicAiChat.subtitle');

  return (
    <>
      {/* Chat paneli */}
      <Slide direction="up" in={open} mountOnEnter unmountOnExit>
        <Paper
          elevation={16}
          sx={{
            position: 'fixed',
            bottom: { xs: 88, sm: 100 },
            right: { xs: 16, sm: 24 },
            width: { xs: 'calc(100vw - 32px)', sm: 380 },
            maxWidth: 'calc(100vw - 32px)',
            height: { xs: 'min(480px, calc(100vh - 200px))', sm: 'min(540px, calc(100vh - 220px))' },
            display: 'flex',
            flexDirection: 'column',
            borderRadius: 3,
            overflow: 'hidden',
            zIndex: 10000,
          }}
        >
          {/* Başlık */}
          <Box
            sx={{
              background: inSupport
                ? 'linear-gradient(135deg, #2e7d32 0%, #43a047 100%)'
                : BRAND_GRADIENTS.primary,
              color: 'white',
              px: 2,
              py: 1.25,
              display: 'flex',
              alignItems: 'center',
              gap: 1,
            }}
          >
            {inSupport ? <SupportAgentIcon fontSize="small" /> : <SmartToyIcon fontSize="small" />}
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Typography variant="subtitle2" fontWeight="bold" noWrap>
                {headerTitle}
              </Typography>
              <Typography variant="caption" sx={{ opacity: 0.85, display: 'block', lineHeight: 1.2 }} noWrap>
                {headerSubtitle}
              </Typography>
            </Box>
            <Tooltip title="WhatsApp" arrow>
              <IconButton
                size="small"
                onClick={openWhatsApp}
                aria-label="WhatsApp ile iletişime geç"
                sx={{
                  color: 'white',
                  bgcolor: '#25D366',
                  '&:hover': { bgcolor: '#20B954' },
                }}
              >
                <WhatsAppIcon fontSize="small" />
              </IconButton>
            </Tooltip>
            <IconButton size="small" onClick={() => setOpen(false)} sx={{ color: 'inherit' }}>
              <CloseIcon fontSize="small" />
            </IconButton>
          </Box>

          {/* Mesajlar */}
          <Box ref={listRef} sx={{ flex: 1, overflowY: 'auto', p: 1.5, bgcolor: 'grey.50' }}>
            {messages.map((m) =>
              m.isSystem ? (
                <Box key={m.id} sx={{ display: 'flex', justifyContent: 'center', mb: 1 }}>
                  <Typography
                    variant="caption"
                    component="div"
                    sx={{
                      color: 'text.secondary',
                      bgcolor: 'grey.200',
                      px: 1.5,
                      py: 0.5,
                      borderRadius: 4,
                      textAlign: 'center',
                      maxWidth: '90%',
                    }}
                  >
                    {renderInline(m.content)}
                  </Typography>
                </Box>
              ) : (
                <Box
                  key={m.id}
                  sx={{
                    display: 'flex',
                    justifyContent: m.role === 'user' ? 'flex-end' : 'flex-start',
                    mb: 1,
                  }}
                >
                  <Box
                    sx={{
                      maxWidth: '85%',
                      px: 1.5,
                      py: 1,
                      borderRadius: 2,
                      bgcolor: m.role === 'user' ? 'primary.main' : m.isError ? 'warning.light' : 'white',
                      color: m.role === 'user' ? 'white' : 'text.primary',
                      boxShadow: m.role === 'user' ? 'none' : '0 1px 4px rgba(0,0,0,0.08)',
                    }}
                  >
                    {m.senderName && (
                      <Typography variant="caption" sx={{ fontWeight: 700, color: 'success.main', display: 'block' }}>
                        {m.senderName}
                      </Typography>
                    )}
                    {m.role === 'assistant' ? (
                      renderAssistantText(m.content)
                    ) : (
                      <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                        {m.content}
                      </Typography>
                    )}
                  </Box>
                </Box>
              )
            )}
            {sending && !inSupport && (
              <Box sx={{ pl: 0.5 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <CircularProgress size={16} />
                  <Typography variant="caption" color="text.secondary">
                    {t('publicAiChat.thinking')}
                  </Typography>
                </Box>
                {slowNotice && (
                  <Typography variant="caption" color="warning.main" sx={{ display: 'block', mt: 0.5 }}>
                    {t('publicAiChat.slowNotice')}
                  </Typography>
                )}
              </Box>
            )}
            {inSupport && supportStatus === 'waiting' && (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, pl: 0.5 }}>
                <CircularProgress size={16} color="success" />
                <Typography variant="caption" color="text.secondary">
                  {t('publicAiChat.supportWaitingSubtitle')}
                </Typography>
              </Box>
            )}
          </Box>

          {/* Hızlı aksiyonlar */}
          <Box sx={{ px: 1.5, pt: 1, display: 'flex', gap: 1, flexWrap: 'wrap' }}>
            {inSupport ? (
              <Button
                size="small"
                variant="outlined"
                color="error"
                onClick={handleEndSupport}
                sx={{ textTransform: 'none' }}
              >
                {t('publicAiChat.supportEnd')}
              </Button>
            ) : (
              <Button
                size="small"
                variant="contained"
                color="success"
                startIcon={supportStarting ? <CircularProgress size={14} color="inherit" /> : <SupportAgentIcon />}
                onClick={startSupport}
                disabled={supportStarting}
                sx={{ textTransform: 'none' }}
              >
                {t('publicAiChat.supportButton')}
              </Button>
            )}
            <Button size="small" variant="outlined" onClick={() => navigate('/contact')} sx={{ textTransform: 'none' }}>
              {t('publicAiChat.contactUs')}
            </Button>
            <Button
              size="small"
              variant="outlined"
              onClick={() => window.open('tel:+903124000912')}
              sx={{ textTransform: 'none' }}
            >
              +90 (312) 400 09 12
            </Button>
          </Box>

          {/* Giriş */}
          <Box sx={{ p: 1.5, display: 'flex', gap: 1, alignItems: 'flex-end' }}>
            <TextField
              fullWidth
              size="small"
              multiline
              maxRows={3}
              placeholder={inSupport ? t('publicAiChat.supportPlaceholder') : t('publicAiChat.placeholder')}
              value={text}
              onChange={(e) => setText(e.target.value.slice(0, inSupport ? 2000 : 500))}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && !e.shiftKey) {
                  e.preventDefault();
                  handleSend();
                }
              }}
              disabled={sending && !inSupport}
            />
            <IconButton color="primary" onClick={handleSend} disabled={(sending && !inSupport) || !text.trim()}>
              <SendIcon />
            </IconButton>
          </Box>
        </Paper>
      </Slide>

      {/* FAB — sağ alt köşe (eski WhatsApp butonunun yeri) */}
      <Zoom in timeout={300}>
        <Tooltip title={t('publicAiChat.fabTooltip')} placement="left" arrow>
          <Fab
            onClick={() => setOpen((p) => !p)}
            aria-label={t('publicAiChat.fabTooltip')}
            sx={{
              position: 'fixed',
              bottom: { xs: 16, sm: 24 },
              right: { xs: 16, sm: 24 },
              width: { xs: 56, sm: 60 },
              height: { xs: 56, sm: 60 },
              zIndex: 9999,
              color: 'white',
              background: BRAND_GRADIENTS.primary,
              boxShadow: '0 4px 20px rgba(21, 101, 192, 0.5)',
              '&:hover': { background: BRAND_GRADIENTS.primary, transform: 'scale(1.08)' },
              transition: 'transform 0.2s ease',
            }}
          >
            {open ? <CloseIcon /> : <SmartToyIcon sx={{ fontSize: 28 }} />}
          </Fab>
        </Tooltip>
      </Zoom>
    </>
  );
};

export default PublicAiChatWidget;
